import type { KeyboardEvent, ReactNode } from 'react';
import { useClickAway } from 'ahooks';
import { Transforms, Range, Editor } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { Center } from '@chakra-ui/react';
import { useRefs } from '@halo/hooks';
import { AvatarAllIcon } from '@halo/ui-chat-react';
import { StateEnum } from '@halo/chat-sdk';
import { Portal } from '@halo/ui-react';
import { useTranslation } from 'react-i18next';
import type { PortalProps } from '../../types';
import { useEditorContext } from '../../scripts/context';
import { Avatar } from '../../../Avatar';
import classes from './Portal.module.scss';
import type { MentionUser } from './types';
import { insertMention } from '.';

export default function MentionPortal({ editableRef, ref }: PortalProps) {
  const { t } = useTranslation();
  const editor = useSlateStatic();
  const { mention: { portal: { filterUsers, onOpenChange, disabled, canAtAll } = {} } = {} } =
    useEditorContext();

  const divRef = useRef<HTMLDivElement | null>(null);
  const [target, setTarget] = useState<Range | null>(null);
  const [groupIndex, setGroupIndex] = useState(0);
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [mentionUsers, setMentionUsers] = useState<MentionUser[]>([]);
  const isOpen = useMemo(() => Boolean(target), [target]);

  useClickAway(
    () => {
      clear();
    },
    [divRef],
    ['click', 'contextmenu']
  );

  const allMembersGroup = useMemo(
    () => ({
      title: '',
      items: [
        {
          id: -1,
          user_id: '*',
          name: `All`,
          username: t('chat.notifyAllGroupMembersText'),
          avatar: '',
          original_name: '',
          remark_name: '',
          state: StateEnum.NORMAL,
        },
      ],
    }),
    [t]
  );

  const chatMembersGroup = useMemo(
    () => ({
      title: t('chat.membersInChatText'),
      items: mentionUsers,
    }),
    [mentionUsers, t]
  );

  const allGroups = useMemo(() => {
    return canAtAll ? [allMembersGroup, chatMembersGroup] : [chatMembersGroup];
  }, [allMembersGroup, chatMembersGroup, canAtAll]);

  const groups = useMemo(
    () => (search ? [chatMembersGroup] : allGroups).filter(({ items }) => items.length),
    [search, chatMembersGroup, allGroups]
  );

  const select = useCallback(
    (args?: { groupIndex: number; index: number }) => {
      if (target) {
        Transforms.select(editor, target);
        const groupIndex_ = args?.groupIndex ?? groupIndex;
        const index_ = args?.index ?? index;
        const groupItem = groups[groupIndex_];
        const item = groupItem?.items[index_];
        if (item) {
          insertMention(editor, item);
        }
        setTarget(null);
      }
    },
    [target, editor, groupIndex, index, groups]
  );

  const onChange = useCallback(() => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection) && !disabled) {
      const [start] = Range.edges(selection);
      const before = Editor.before(editor, start, { unit: 'line' });
      const beforeRange = before && Editor.range(editor, before, start);
      const beforeText = beforeRange && Editor.string(editor, beforeRange);
      const beforeMatch = beforeText && beforeText.match(/@([^@\s]*)$/);

      const after = Editor.after(editor, start);
      const afterRange = Editor.range(editor, start, after);
      const afterText = Editor.string(editor, afterRange);
      const afterMatch = /^(\W|\s|$)/.test(afterText);

      if (beforeMatch && afterMatch) {
        const [match, $1] = beforeMatch;
        const range = Editor.range(
          editor,
          { ...start, offset: start.offset - match.length },
          start
        );

        setTarget(range);
        setSearch($1);
        setIndex(0);

        return;
      }
    }

    setTarget(null);
  }, [editor, disabled]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (target && groups.length > 0) {
        const items = groups[groupIndex].items;
        let groupIndex_ = groupIndex;
        let index_ = index;

        switch (event.key) {
          case 'ArrowDown': {
            event.preventDefault();

            index_ = index + 1;

            if (index_ >= items.length) {
              groupIndex_ = (groupIndex + 1) % groups.length;
              index_ = 0;
            }

            setGroupIndex(groupIndex_);
            setIndex(index_);
            break;
          }
          case 'ArrowUp': {
            event.preventDefault();

            index_ = index - 1;

            if (index_ <= -1) {
              groupIndex_ = groupIndex - 1;
              if (groupIndex_ <= -1) {
                groupIndex_ = groups.length - 1;
              }
              index_ = groups[groupIndex_].items.length - 1;
            }

            setGroupIndex(groupIndex_);
            setIndex(index_);
            break;
          }
          case 'Tab':
          case 'Enter':
            event.preventDefault();
            select();
            break;
          case 'Escape':
            event.preventDefault();
            setTarget(null);
            break;
        }
      }
    },
    [target, groups, groupIndex, index, select]
  );

  const clear = useCallback(() => {
    setTarget(null);
    setSearch('');
  }, []);

  const refs = useRefs({ onOpenChange });

  useImperativeHandle(ref, () => ({ onChange, onKeyDown }), [onChange, onKeyDown]);

  useLayoutEffect(() => {
    if (target && groups.length > 0 && divRef.current) {
      const popoverDom = divRef.current;
      const popoverDomRect = popoverDom.getBoundingClientRect();

      const selectionRange = ReactEditor.toDOMRange(editor, target);
      const selectionRect = selectionRange.getBoundingClientRect();

      const editableDom = editableRef.current!.parentElement!;
      const editableRect = editableDom.getBoundingClientRect();

      const viewportHeight = window.innerHeight;
      const spaceAbove = selectionRect.top;

      const maxContentHeight = Math.min(380, viewportHeight - 100);
      const dynamicMaxHeight = Math.min(maxContentHeight, spaceAbove - 20);

      const contentElement = popoverDom.querySelector(`.${classes.content}`) as HTMLElement;
      if (contentElement) {
        contentElement.style.maxHeight = `${Math.max(200, dynamicMaxHeight)}px`;
      }

      const topPosition = selectionRect.top + window.scrollY - popoverDomRect.height;
      const finalTop = Math.max(10, topPosition);

      popoverDom.style.cssText = `
        top: ${finalTop}px;
        left: ${editableRect.left - 10}px;
        right: '20px';
        // width: ${editableRect.width}px;
      `;
    }
  }, [editor, target, editableRef, groups]);

  useLayoutEffect(() => {
    setGroupIndex(0);
    setIndex(0);
  }, [groups]);

  useEffect(() => {
    const activeDom = divRef.current?.querySelector(`.${classes.active}`);
    if (activeDom) {
      activeDom.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [groupIndex, index, groups]);

  useLayoutEffect(() => {
    (async () => {
      let result: MentionUser[] = [];
      if (target && filterUsers) {
        const members = (await filterUsers(search)).filter(
          (m) => m.name && m.state !== StateEnum.DELETE_ACCOUNT
        );
        if (search) {
          const reg = new RegExp(search, 'i');
          result = members.filter((m) => reg.test(m.name) || reg.test(m.username!));
        } else {
          result = members;
        }
      }
      setMentionUsers(result);
    })();
  }, [search, target, filterUsers]);

  useLayoutEffect(() => {
    const { onOpenChange } = refs.current;
    onOpenChange?.(isOpen);
  }, [isOpen, refs]);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      if (target && groups.length > 0 && divRef.current) {
        const popoverDom = divRef.current;
        const popoverDomRect = popoverDom.getBoundingClientRect();

        const selectionRange = ReactEditor.toDOMRange(editor, target);
        const selectionRect = selectionRange.getBoundingClientRect();

        const editableDom = editableRef.current!.parentElement!;
        const editableRect = editableDom.getBoundingClientRect();

        const viewportHeight = window.innerHeight;
        const spaceAbove = selectionRect.top;

        const maxContentHeight = Math.min(380, viewportHeight - 100);
        const dynamicMaxHeight = Math.min(maxContentHeight, spaceAbove - 20);

        const contentElement = popoverDom.querySelector(`.${classes.content}`) as HTMLElement;
        if (contentElement) {
          contentElement.style.maxHeight = `${Math.max(200, dynamicMaxHeight)}px`;
        }

        const topPosition = selectionRect.top + window.scrollY - popoverDomRect.height;
        const finalTop = Math.max(10, topPosition);

        popoverDom.style.cssText = `
          top: ${finalTop}px;
          left: ${editableRect.left - 10}px;
          right: '20px';
          // width: ${editableRect.width}px;
        `;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, target, groups, editor, editableRef]);

  return (
    target &&
    groups.length > 0 && (
      <Portal>
        <div
          ref={divRef}
          className={classes.popover}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <div className={`${classes.content} scrollHover`}>
            {groups.map(({ title, items }, i) => (
              <div key={title} className={classes.group}>
                {title && <div className={classes.group_title}>{title}</div>}
                {items.map((item, j) => {
                  let avatar: ReactNode;
                  let name: ReactNode;
                  let username: ReactNode;

                  if (item.user_id === '*') {
                    avatar = (
                      <Center w="9">
                        <AvatarAllIcon />
                      </Center>
                    );
                    name = `${item.name} (${mentionUsers.length})`;
                    username = item.username;
                  } else {
                    avatar = <Avatar src={item.avatar} name={item.name} />;
                    name = item.name;
                    username = `@${item.username}`;
                  }

                  return (
                    <div
                      className={cls(classes.item, {
                        [classes.active]: i === groupIndex && j === index,
                        [classes.deleted]: !item.name,
                      })}
                      key={item.user_id}
                      onClick={() => {
                        if (!item.name) return;
                        select({
                          groupIndex: i,
                          index: j,
                        });
                      }}
                    >
                      {avatar}
                      <div className={classes.userInfo}>
                        <div className={classes.name}>{name}</div>
                        <div className={classes.username}>{username}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Portal>
    )
  );
}
